/**
 * CustomerDetailSidebar — right-column cards for the Shopify-style Customer
 * detail page. Shows COMPLEMENTARY, computed data the main ExperienceView
 * (raw ERPNext fields) doesn't: a financial summary, recent order history,
 * contact info, and the primary address.
 *
 * Read-only by design. Customer contact/address fields (email_id, mobile_no,
 * primary_address) are ERPNext read-only fetch fields sourced from the primary
 * Contact/Address — editing them here would not persist, so we display only.
 * Editable ERPNext fields remain fully editable in the main column.
 *
 * Uses the same CARD design token as the ExperienceView widgets for visual
 * consistency with the Sales Order page's OrderDetailSidebar.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, ChevronLeft, Receipt, ShoppingBag, ShoppingCart, Award, Star,
} from "lucide-react";
import { CARD } from "@/config/design-tokens";
import { cn } from "@/lib/utils";
import { routes } from "@/lib/config/routes";
import { useCustomerOrders, useCustomerActivity, type CustomerActivity } from "@/features/customers/api/customerHooks";
import { useCustomerPointHistory } from "@/features/rewards/api/rewardsAdminHooks";
import { useDetailPageTabs } from "@/contexts/DetailPageTabsContext";
import { LOYALTY_TAB_ID } from "./useLoyaltyTab";
import { MemberCardForTier } from "./MemberCardForTier";
import { parseLoginIdentities, type PortalUserRow } from "./AccountIdentityCard";
import { ActivityTimelineCard } from "./ActivityTimelineCard";
import { CardTitle, formatMoney, formatReadableDate, formatShortDate } from "./customerDetailPrimitives";
import type { SalesOrder } from "@/lib/erp/types/order";

const STATUS_STYLES: Record<string, string> = {
  "To Deliver and Bill": "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  "To Bill": "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  "To Deliver": "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  Completed: "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300",
  Closed: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  Cancelled: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  Draft: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

function StatusPill({ status }: { status?: string }) {
  if (!status) return null;
  const style = STATUS_STYLES[status] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
  return (
    <span className={cn("text-[10px] font-medium rounded-full px-2 py-0.5 shrink-0", style)}>
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Stat tile — subtle grouped tile used in the 2-column Financial Summary grid
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Loyalty — compact points/tier/program summary + jump to the full Loyalty tab
// ---------------------------------------------------------------------------

function LoyaltyCard({
  customer,
  tier,
  program,
  storefrontKey,
}: {
  customer: string;
  tier?: string;
  program?: string;
  storefrontKey?: string;
}) {
  // Same default limit as the Loyalty tab so they share the React Query cache
  // (the query key ignores limit — a smaller limit here would truncate the tab).
  const { data, isLoading } = useCustomerPointHistory(customer, 50, 0, !!customer);
  const { setActiveTab } = useDetailPageTabs();
  const balance = data?.current_balance ?? 0;

  // Recent activity preview — top few meaningful (non-zero) point entries
  const recent = (data?.entries ?? []).filter((e) => Number(e.points) !== 0).slice(0, 3);
  const hasEntries = (data?.entries?.length ?? 0) > 0;

  return (
    <div className={CARD}>
      <div className="p-5">
        <CardTitle icon={<Award className="h-3.5 w-3.5" />}>Loyalty</CardTitle>

        {/* The customer's actual storefront member card */}
        <div className="mb-4">
          <MemberCardForTier tier={tier} storefrontKey={storefrontKey} mini />
        </div>

        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {isLoading ? "…" : balance.toLocaleString()}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Points balance</div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              Level
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded-full px-2.5 py-1">
              <Star className="h-3 w-3" />
              {tier || "—"}
            </span>
          </div>
        </div>

        {program && (
          <div className="mt-3 flex items-center gap-1.5 text-xs min-w-0">
            <span className="text-muted-foreground shrink-0">Program</span>
            <span className="font-medium text-foreground truncate">{program}</span>
          </div>
        )}

        {/* Recent activity preview */}
        {!isLoading && (
          <div className="mt-4">
            <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Recent activity
            </div>
            {recent.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">
                {hasEntries ? "No point changes yet" : "No loyalty activity yet"}
              </div>
            ) : (
              <div className="space-y-1.5">
                {recent.map((e) => {
                  const positive = Number(e.points) > 0;
                  const label = e.description
                    ? `${e.earn_source} · ${e.description}`
                    : e.earn_source;
                  return (
                    <div key={e.name} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-foreground truncate min-w-0">{label}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span
                          className={cn(
                            "text-xs font-semibold font-mono",
                            positive
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-500 dark:text-red-400",
                          )}
                        >
                          {positive ? "+" : ""}
                          {Number(e.points).toLocaleString()}
                        </span>
                        <span className="text-[10px] text-muted-foreground w-12 text-right">
                          {formatShortDate(e.posting_date)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setActiveTab(LOYALTY_TAB_ID)}
          className="mt-3 pt-2.5 border-t border-border/40 w-full flex items-center justify-center gap-1 text-[11px] font-medium text-primary hover:text-primary/80 transition-colors"
        >
          View full history
          <ChevronRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Order History — recent orders, click through to the order page
// ---------------------------------------------------------------------------

const ORDERS_PER_PAGE = 6;

function OrderHistoryCard({
  orders,
  isLoading,
  currency,
  orderThumbs,
}: {
  orders: SalesOrder[];
  isLoading: boolean;
  currency: string;
  /** {order_name: first-item image URL} — a visual guide per order row. */
  orderThumbs?: Record<string, string>;
}) {
  const [page, setPage] = useState(0);
  const sorted = [...orders].sort((a, b) => {
    const da = (a as Record<string, any>).creation || (a as Record<string, any>).transaction_date || "";
    const db = (b as Record<string, any>).creation || (b as Record<string, any>).transaction_date || "";
    return db.localeCompare(da);
  });
  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / ORDERS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * ORDERS_PER_PAGE;
  const shown = sorted.slice(start, start + ORDERS_PER_PAGE);

  return (
    <div className={CARD}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5">
            <span className="text-gray-400 dark:text-gray-500"><ShoppingBag className="h-3.5 w-3.5" /></span>
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Order History
            </span>
          </div>
          {!isLoading && sorted.length > 0 && (
            <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 bg-muted/60 rounded-full px-2 py-0.5">
              {sorted.length}
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="flex flex-col items-center py-4 text-center">
            <Receipt className="h-6 w-6 text-gray-300 dark:text-gray-600 mb-2" />
            <span className="text-sm text-muted-foreground">No orders yet</span>
          </div>
        ) : (
          <div className="space-y-0.5 -mx-2">
            {shown.map((o) => {
              const rec = o as Record<string, any>;
              const qty = Number(rec.total_qty) || 0;
              const meta = [
                formatReadableDate(rec.transaction_date),
                qty > 0 ? `${qty % 1 === 0 ? qty : qty.toFixed(0)} item${qty === 1 ? "" : "s"}` : null,
              ]
                .filter(Boolean)
                .join(" · ");
              const thumb = orderThumbs?.[rec.name];
              return (
                <Link
                  key={rec.name}
                  to={routes.salesOrder.detail(rec.name)}
                  className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/50 transition-colors group"
                >
                  {/* Leading item thumbnail — a visual guide for the order */}
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      loading="lazy"
                      className="h-9 w-9 shrink-0 rounded-lg object-cover ring-1 ring-black/5 dark:ring-white/10"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                      <ShoppingBag className="h-4 w-4" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {rec.name}
                      </span>
                      <StatusPill status={rec.status} />
                    </div>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">{meta}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
                      {formatMoney(rec.grand_total, rec.currency || currency)}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-gray-500" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {!isLoading && total > ORDERS_PER_PAGE && (
          <div className="mt-2 flex items-center justify-between border-t border-border/40 pt-2.5">
            <span className="text-[11px] text-muted-foreground">
              {start + 1}–{Math.min(start + ORDERS_PER_PAGE, total)} จาก {total}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                aria-label="ก่อนหน้า"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                aria-label="ถัดไป"
                className="flex h-7 w-7 items-center justify-center rounded-lg border border-border/60 text-muted-foreground transition-colors hover:bg-muted/50 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Cart — the customer's current (or abandoned) storefront cart
// ---------------------------------------------------------------------------

function CartCard({ cart, currency }: { cart: CustomerActivity["cart"]; currency: string }) {
  if (!cart || !cart.items?.length) return null;
  const extra = cart.items.length - 5;
  return (
    <div className={CARD}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5">
            <ShoppingCart className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {cart.abandoned ? "Abandoned Cart" : "Current Cart"}
            </span>
          </div>
          {cart.abandoned && (
            <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded-full px-2 py-0.5">
              {cart.age_days}d idle
            </span>
          )}
        </div>

        <div className="space-y-1.5">
          {cart.items.slice(0, 5).map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span className="text-[13px] text-foreground truncate">
                {it.qty}× {it.item_name || it.item_code}
              </span>
              <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 shrink-0">
                {formatMoney(it.amount, currency)}
              </span>
            </div>
          ))}
          {extra > 0 && <div className="text-[11px] text-muted-foreground">+{extra} more</div>}
        </div>

        <div className="mt-3 pt-2.5 border-t border-border/40 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            {cart.total_qty} item{cart.total_qty === 1 ? "" : "s"} · {formatShortDate(cart.updated)}
          </span>
          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
            {formatMoney(cart.grand_total, currency)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function CustomerDetailSidebar({ formData }: { formData: Record<string, any> }) {
  const customerName = (formData.name as string) || "";
  const currency = (formData.default_currency as string) || "THB";

  const { data: orders, isLoading } = useCustomerOrders(customerName);
  const orderList = orders ?? [];

  const lineUserId = parseLoginIdentities(
    formData.portal_users as PortalUserRow[] | undefined,
  ).find((l) => l.method === "line")?.lineUserId;

  const { data: activity } = useCustomerActivity(customerName);

  return (
    <div className="space-y-4">
      <LoyaltyCard
        customer={customerName}
        tier={formData.loyalty_program_tier as string | undefined}
        program={formData.loyalty_program as string | undefined}
        storefrontKey={formData.custom_primary_storefront as string | undefined}
      />
      <CartCard cart={activity?.cart ?? null} currency={currency} />
      <OrderHistoryCard orders={orderList} isLoading={isLoading} currency={currency} orderThumbs={activity?.order_thumbs} />
      <ActivityTimelineCard
        customer={customerName}
        currency={currency}
        lineUserId={lineUserId}
        browsing={activity?.browsing}
        lastSeen={activity?.last_seen}
        customerSince={formData.creation as string | undefined}
        orderThumbs={activity?.order_thumbs}
        collectionThumbs={activity?.collection_thumbs}
      />
    </div>
  );
}
